import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import {
  signUpWithEmail,
  signInWithEmail,
  resetPassword,
  auth,
  signInWithMicrosoft,
  signInWithGoogle
} from '../lib/firebase';
import { useLocation, useNavigate } from 'react-router-dom';
import '../CSS/auth.css';
import apiClient from '../lib/api-client';
import { sendEmailVerification, signInWithEmailAndPassword } from '@firebase/auth';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../AuthContext';
import logoTag from '../assets/logo-rof.svg';
import microsoftLogo from '../assets/microsoft_logo.svg';
import googleLogo from '../assets/google_logo.svg';



function AuthPage() {
  const [showSignup, setShowSignup] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' });
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [authMessage, setAuthMessage] = useState({ type: '', text: '' });
  const { theme, toggleTheme } = useTheme();
  const { checkAuth } = useAuth();

  // Add these state variables at the top with your other state declarations
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  const navigate = useNavigate();
  const location = useLocation();

  // Correctly convert string environment variable to boolean 
  const allow_personal_domains = process.env.REACT_APP_ALLOW_PERSONAL_DOMAINS === 'true';
  const is_env_verification = process.env.REACT_APP_IS_ENV_VERIFICATION === 'true';


  let commonEmailDomains;

  if (allow_personal_domains) {
    // console.log('Using empty domain blocklist (personal emails allowed)');
    commonEmailDomains = [];
  } else {
    // console.log('Using domain blocklist (personal emails restricted)');
    commonEmailDomains = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
      'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'ymail.com',
      'me.com', 'live.com', 'inbox.com', 'gmx.com', 'fastmail.com',
      'tutanota.com', 'hushmail.com', 'mail.ru', 'yandex.com', 'msn.com',
      'rediffmail.com', 'rocketmail.com', 'optonline.net', 'comcast.net',
      'cox.net', 'earthlink.net', 'bellsouth.net', 'charter.net', 'shaw.ca',
      'rogers.com', 'naver.com', 'qq.com', '126.com', '163.com', 'yeah.net',
      'lycos.com', 'zoznam.sk', 'seznam.cz', 'bluewin.ch'
    ];
  }



  // Function to validate work email
  const validateWorkEmail = (email) => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain || domain === '') return false;

    return !commonEmailDomains.includes(domain);
  };

  // Updated email change handler
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setSignupEmail(value);

    if (value && value.includes('@')) {

      const domain = value.split('@')[1]?.toLowerCase();

      if (domain && commonEmailDomains.includes(domain)) {
        setEmailError('Please use your work email address.');
      } else {
        setEmailError('');
      }
    } else {
      setEmailError('');
    }
  };

  const toggleForms = () => {
    setShowSignup(!showSignup);
    setVerificationSent(false);
    setSignupPassword('');
    setConfirmPassword('');
    setPasswordMismatch(false);
  };

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password)
    };

    setPasswordRequirements(requirements);

    // Return true if all requirements are met
    return Object.values(requirements).every(Boolean);
  };

  // Update the password change handler
  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setSignupPassword(password);
    validatePassword(password);
  };

  // In your AuthPage component
  useEffect(() => {
    // Check if user was redirected due to session expiry
    const urlParams = new URLSearchParams(window.location.search);
    const expired = urlParams.get('expired');

    if (expired === 'true') {
      setAuthMessage({
        type: 'warning',
        text: 'Your session has expired. Please log in again.'
      });

      // Clear the query parameter
      window.history.replaceState(null, '', '/auth');
    }
  }, []);


  useEffect(() => {
    if (confirmPassword) {
      setPasswordMismatch(signupPassword !== confirmPassword);
    } else {
      setPasswordMismatch(false);
    }
  }, [signupPassword, confirmPassword]);

  useEffect(() => {
    if (location.state?.message) {
      alert(location.state.message);
      // Clear the state to prevent showing the message again
      navigate(location.pathname, {
        replace: true,
        state: {}
      });
    }
  }, [location, navigate]);


  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      const result = await signInWithGoogle();

      if (result.user) {
        const token = await result.user.getIdToken();
        const uid = result.user.uid;
        const email = result.user.email;

        try {
          // Try to login first
          await loginUser(email, null, token);
        } catch (loginErr) {
          // If login fails, try registering
          console.log('Registering new Google user...');
          await apiClient.register({
            email: email,
            uid: uid,
            token: token,
            auth_provider: 'google'
          });

          // Then try logging in again
          await loginUser(email, null, token);
        }
      }
    } catch (error) {
      console.error('Google login error:', error);
      alert(`Google login failed: ${error.message || 'Please try again'}`);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsMicrosoftLoading(true);
    try {
      const result = await signInWithMicrosoft();

      // After successful Microsoft auth, register or login with backend
      if (result.user) {
        const token = await result.user.getIdToken();
        const uid = result.user.uid;
        const email = result.user.email;

        try {
          // Try to login first (for existing users)
          await loginUser(email, null, token);
        } catch (loginErr) {
          // If login fails, try registering the user
          console.log('Registering new Microsoft user...');
          await apiClient.register({
            email: email,
            uid: uid,
            token: token,
            auth_provider: 'microsoft'
          });

          // Then try logging in again
          await loginUser(email, null, token);
        }
      }
    } catch (error) {
      console.error('Microsoft login error:', error);
      alert(`Microsoft login failed: ${error.message || 'Please try again'}`);
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (signupPassword !== confirmPassword) {
      setPasswordMismatch(true);
      return;
    }

    // Validate work email
    if (!validateWorkEmail(signupEmail)) {
      setEmailError('Please use your work email address.');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Starting signup...');

      const userCredential = await signUpWithEmail(signupEmail, signupPassword);

      const token = await userCredential.user.getIdToken();
      const uid = userCredential.user.uid;
      console.log('User UID:', uid);
      const backendResponse = await apiClient.register({
        email: signupEmail,
        uid: uid,
        token: token
      });

      if (is_env_verification) {
        // Send verification email 
        await sendEmailVerification(userCredential.user);
        console.log('Verification email sent');
        setVerificationSent(true);
      } else {
        // If no verification needed, log in directly using the token we already have
        await new Promise(resolve => setTimeout(resolve, 1000));
        await loginUser(signupEmail, null, token);
      }
    } catch (error) {
      // Improved error messages
      let message = error.message;
      if (error.code === 'Firebase: Error (auth/email-already-in-use)') {
        message = 'Email already in use';
      } else if (error.code === 'auth/invalid-email') {
        message = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        message = 'Password should be at least 6 characters';
      }

      alert(`Signup failed : ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // 1. Firebase Authentication
      console.log('Attempting Firebase login...');
      const userCredential = await signInWithEmailAndPassword(
        auth,
        loginEmail,
        loginPassword
      );

      // 2. Email Verification Check
      if (is_env_verification && !userCredential.user.emailVerified) {
        setSignupEmail(loginEmail);
        setAuthMessage({ type: '', text: '' });
        setVerificationSent(true);
        return;
      }

      // 3. Get ID Token
      console.log('Getting ID token...');
      const token = await userCredential.user.getIdToken();
      console.log('Token obtained, length:', token.length);

      // 4. Backend Authentication
      console.log('Calling backend API...');
      await loginUser(loginEmail, loginPassword);

    } catch (error) {
      console.error('Full error stack:', error);

      // Improved error messages
      let message = error.message;
      if (error.code === 'auth/invalid-credential') message = 'Incorrect email or password';
      if (error.code === 'auth/user-not-found') message = 'Email not registered';
      if (error.code === 'auth/too-many-requests') message = 'Please check your email for a verification link';

      alert(`Login failed: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loginUser = async (email, password, token = null) => {
    const loginResponse = await (token
      ? apiClient.loginWithToken(token)
      : apiClient.login(email, password));

    console.log('Login response:', loginResponse);
    localStorage.setItem('access_token', loginResponse.access_token);
    localStorage.setItem('uid', loginResponse.uid);

    // Refresh auth context so it picks up the new token
    await checkAuth();

    if (!loginResponse.is_info) {
      navigate('/user/user-details', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleForgotPassword = () => {
    setResetEmail(loginEmail); // Pre-fill with login email if available
    setResetMessage({ type: '', text: '' });
    setShowResetModal(true);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setResetMessage({
        type: 'danger',
        text: 'Please enter your email address'
      });
      return;
    }

    setIsResetting(true);
    setResetMessage({ type: '', text: '' });

    try {
      await resetPassword(resetEmail);
      setResetMessage({
        type: 'success',
        text: 'Password reset email sent. Please check your inbox and spam folder.'
      });

      // Clear form after successful submission
      setTimeout(() => {
        setShowResetModal(false);
        setResetMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);

      // Provide friendly error message
      let errorMsg = 'Failed to send reset email. Please try again.';
      if (error.code === 'auth/user-not-found') {
        errorMsg = 'No account found with this email address.';
      }

      setResetMessage({ type: 'danger', text: errorMsg });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="auth-container position-relative">
      {/* Theme Toggle Button */}
      <Button
        variant="light"
        className="position-absolute top-0 end-0 m-4 shadow-sm"
        onClick={toggleTheme}
        style={{ zIndex: 1050, background: 'var(--bg-card)', color: 'var(--text-main)', border: '1px solid var(--border-color)' }}
        title={theme === 'dark' ? "Switch to Light Mode" : "Switch to Dark Mode"}
      >
        <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'} fs-5`}></i>
      </Button>

      {/* Password Reset Modal */}
      <Modal
        show={showResetModal}
        onHide={() => setShowResetModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Enter your email address and we'll send you instructions to reset your password.</p>

          {resetMessage.text && (
            <div className={`alert alert-${resetMessage.type}`}>
              {resetMessage.text}
            </div>
          )}

          <Form onSubmit={handlePasswordReset}>
            <Form.Group className="mb-3">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button
                variant="primary"
                type="submit"
                disabled={isResetting}
              >
                {isResetting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <Button
                variant="outline-secondary"
                onClick={() => setShowResetModal(false)}
                disabled={isResetting}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <div className="row auth-card">
        {/* Left side - Image */}
        <div className="col-md-6 auth-image">
          <div className="text-center">
            <img src={logoTag} alt="Logo" className="mb-2" style={{ height: '120px' }} />
          </div>
        </div>


        {/* Right side - Forms */}
        <div className="col-md-6 p-5">
          <div className="text-center mb-4">
            <h2 className="text-primary mb-3">
              {verificationSent ? 'Verify Your Email' : showSignup ? 'Create Account' : 'Sign In'}
            </h2>

            {/* Display authentication messages */}
            {authMessage.text && (
              <div className={`alert alert-${authMessage.type}`}>
                {authMessage.text}
              </div>
            )}

            {verificationSent && (
              <div className="alert alert-info">
                <div className="mb-3">
                  <i className="fas fa-envelope-open-text me-2"></i>
                  We've sent a verification email to <strong>{signupEmail}</strong>.
                  Please verify your email before logging in.
                </div>
                <div className="d-flex justify-content-center mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={toggleForms}
                  >
                    <i className="fas fa-sign-in-alt me-2"></i>
                    Return to Login
                  </button>
                </div>
                <div className="text-center mt-2 small">
                  <span className="text-muted">Didn't receive an email?</span>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-1 text-decoration-none"
                    onClick={async () => {
                      try {
                        // Get current user
                        const user = auth.currentUser;
                        if (user) {
                          await sendEmailVerification(user);
                          alert('Verification email sent again. Please check your inbox and spam folder.');
                        } else {
                          alert('Please sign up again to receive a verification email.');
                          setVerificationSent(false);
                          setShowSignup(true);
                        }
                      } catch (error) {
                        console.error('Error resending email:', error);
                        alert('Failed to resend verification email. Please try again later.');
                      }
                    }}
                  >
                    Resend email
                  </button>
                </div>
              </div>
            )}
          </div>

          {!verificationSent && (
            <>
              <div className="d-flex flex-column gap-2 mb-4">
                <button
                  className="btn btn-light w-100 position-relative border d-flex align-items-center justify-content-center py-2 social-login-btn"
                  onClick={handleGoogleLogin}
                  disabled={isGoogleLoading || isMicrosoftLoading || isLoading}
                  style={{
                    transition: "all 0.2s ease",
                    boxShadow: "none"
                  }}
                  onMouseOver={(e) => e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)"}
                  onMouseOut={(e) => e.currentTarget.style.boxShadow = "none"}
                >
                  {isGoogleLoading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : (
                    <img
                      src={googleLogo}
                      alt="Google"
                      style={{ height: '20px', width: '20px' }}
                      className="me-2"
                    />
                  )}
                  {isGoogleLoading ? 'Connecting...' : 'Continue with Google'}
                </button>

                <button
                  className="btn btn-light w-100 position-relative border d-flex align-items-center justify-content-center py-2 social-login-btn"
                  onClick={handleMicrosoftLogin}
                  disabled={isMicrosoftLoading || isGoogleLoading || isLoading}
                  style={{
                    transition: "all 0.2s ease",
                    boxShadow: "none"
                  }}
                  onMouseOver={(e) => e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)"}
                  onMouseOut={(e) => e.currentTarget.style.boxShadow = "none"}
                >
                  {isMicrosoftLoading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : (
                    <img
                      src={microsoftLogo}
                      alt="Microsoft"
                      style={{ height: '20px', width: '20px' }}
                      className="me-2"
                    />
                  )}
                  {isMicrosoftLoading ? 'Connecting...' : 'Continue with Microsoft'}
                </button>
              </div>

              <div className="divider">
                <span>or continue with email</span>
              </div>

              {/* Login Form */}
              {!showSignup ? (
                <form id="login-form" className="mb-4" onSubmit={handleLogin}>
                  <div className="mb-3">
                    <input
                      type="email"
                      id="login-email"
                      className="form-control"
                      placeholder="Email address"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3 position-relative">
                    <input
                      type={showLoginPassword ? "text" : "password"}
                      id="login-password"
                      className="form-control pe-5"
                      placeholder="Password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2 p-0 text-muted"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      tabIndex="-1"
                      style={{ zIndex: 10 }}
                    >
                      <i className={`fas fa-${showLoginPassword ? 'eye-slash' : 'eye'}`}></i>
                    </button>
                  </div>

                  <div className="d-flex justify-content-between mb-3">

                    {/* <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="remember"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="remember">Remember me</label>
                    </div> */}

                    <button
                      type="button"
                      className="btn btn-link text-primary text-decoration-none p-0"
                      onClick={handleForgotPassword}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <button type="submit" className="btn btn-primary w-100 mb-4" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </button>

                  <div className="text-center">
                    <p className="mb-0 text-muted">
                      Don't have an account?
                      <button
                        type="button"
                        className="btn btn-link text-primary text-decoration-none p-0 ms-1"
                        onClick={toggleForms}
                      >
                        Create account
                      </button>
                    </p>
                  </div>
                </form>
              ) : (
                /* Sign Up Form */
                <form id="signup-form" onSubmit={handleSignup}>
                  <div className="mb-3">
                    <label htmlFor="signup-email" className="form-label small text-muted">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="signup-email"
                      className={`form-control ${emailError ? 'is-invalid' : ''}`}
                      placeholder="your.name@company.com"
                      value={signupEmail}
                      onChange={handleEmailChange}
                      required
                    />
                    {emailError && (
                      <div className="invalid-feedback">
                        <i className="fas fa-exclamation-circle me-1"></i>
                        {emailError}
                      </div>
                    )}
                  </div>

                  <div className="mb-3 position-relative">
                    <label htmlFor="signup-password" className="form-label small text-muted">
                      Password
                    </label>
                    <input
                      type={showSignupPassword ? "text" : "password"}
                      id="signup-password"
                      className="form-control pe-5"
                      placeholder="Create a password"
                      value={signupPassword}
                      onChange={handlePasswordChange}
                      required
                      minLength="8"
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-0 mt-3 me-2 p-0 text-muted"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                      tabIndex="-1"
                      title={showSignupPassword ? "Hide password" : "Show password"}
                      style={{ height: '100%' }}
                    >
                      <i className={`fas fa-${showSignupPassword ? 'eye-slash' : 'eye'}`}></i>
                    </button>
                  </div>

                  <div className="mb-3 position-relative">
                    <label htmlFor="confirm-password" className="form-label small text-muted">
                      Confirm Password
                    </label>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirm-password"
                      className={`form-control pe-5 ${passwordMismatch ? 'is-invalid' : ''}`}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-0 mt-3 me-2 p-0 text-muted"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex="-1"
                      title={showConfirmPassword ? "Hide password" : "Show password"}
                      style={{ height: '100%' }}
                    >
                      <i className={`fas fa-${showConfirmPassword ? 'eye-slash' : 'eye'}`}></i>
                    </button>
                    {passwordMismatch && (
                      <div className="invalid-feedback d-block">
                        <i className="fas fa-exclamation-circle me-1"></i>
                        Passwords don't match
                      </div>
                    )}
                  </div>


                  {/* Password requirements indicators */}
                  <div className="password-requirements mt-2">
                    <small className="d-block mb-1">Password must contain:</small>
                    <ul className="list-unstyled small ps-2">
                      <li className={`mb-1 ${passwordRequirements.length ? 'text-success' : 'text-muted'}`}>
                        <i className={`fas fa-${passwordRequirements.length ? 'check' : 'times'} me-1`}></i>
                        At least 8 characters
                      </li>
                      <li className={`mb-1 ${passwordRequirements.uppercase ? 'text-success' : 'text-muted'}`}>
                        <i className={`fas fa-${passwordRequirements.uppercase ? 'check' : 'times'} me-1`}></i>
                        At least one uppercase letter
                      </li>
                      <li className={`mb-1 ${passwordRequirements.lowercase ? 'text-success' : 'text-muted'}`}>
                        <i className={`fas fa-${passwordRequirements.lowercase ? 'check' : 'times'} me-1`}></i>
                        At least one lowercase letter
                      </li>
                      <li className={`mb-1 ${passwordRequirements.number ? 'text-success' : 'text-muted'}`}>
                        <i className={`fas fa-${passwordRequirements.number ? 'check' : 'times'} me-1`}></i>
                        At least one number
                      </li>
                      <li className={`mb-1 ${passwordRequirements.special ? 'text-success' : 'text-muted'}`}>
                        <i className={`fas fa-${passwordRequirements.special ? 'check' : 'times'} me-1`}></i>
                        At least one special character
                      </li>
                    </ul>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100 mb-4"
                    disabled={
                      isLoading ||
                      passwordMismatch ||
                      !signupPassword ||
                      !confirmPassword ||
                      emailError ||
                      !Object.values(passwordRequirements).every(Boolean)
                    }
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Creating account...
                      </>
                    ) : (
                      'Sign Up'
                    )}
                  </button>

                  <div className="text-center">
                    <p className="mb-0">
                      Already have an account?
                      <button
                        type="button"
                        className="btn btn-link text-primary text-decoration-none p-0 ms-1"
                        onClick={toggleForms}
                      >
                        Sign in
                      </button>
                    </p>
                  </div>
                </form>
              )}
            </>
          )}
          {/* <button onClick={() => navigate(-1)} className="btn btn-link">
            Back
          </button> */}
          {/* Guest escape hatch */}
          {!verificationSent && (
            <div className="text-center mt-4">
              <div style={{ height: '1px', background: 'var(--border-color)', margin: '0 0 1rem' }} />
              <button
                type="button"
                className="btn btn-link text-muted text-decoration-none p-0 small"
                onClick={() => navigate('/')}
                style={{ fontSize: '0.85rem' }}
              >
                Explore without signing in <i className="fas fa-arrow-right ms-1" style={{ fontSize: '11px' }} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
